import type { SVGProps } from 'react';

type Part =
  | ['path', { d: string }]
  | ['circle', { cx: number; cy: number; r: number }]
  | ['rect', { x: number; y: number; width: number; height: number; rx?: number }];

const p = (...d: string[]): Part[] => d.map((x) => ['path', { d: x }]);

export const GLYPHS = {
  back: p('M15 5l-7 7 7 7'),
  sun: [
    ['circle', { cx: 12, cy: 12, r: 4 }],
    ...p(
      'M12 2v2M12 20v2M2 12h2M20 12h2M4.5 4.5l1.4 1.4M18.1 18.1l1.4 1.4M19.5 4.5l-1.4 1.4M5.9 18.1l-1.4 1.4',
    ),
  ],
  moon: p('M20 13.5A8 8 0 0 1 10.5 4 8 8 0 1 0 20 13.5Z'),
  sliders: [
    ...p('M4 7h9M17 7h3M4 17h3M11 17h9'),
    ['circle', { cx: 15, cy: 7, r: 2.2 }],
    ['circle', { cx: 9, cy: 17, r: 2.2 }],
  ],
  camera: [
    ...p(
      'M3 8.5A2.5 2.5 0 0 1 5.5 6h1.2l1.1-2h6.4l1.1 2h1.2A2.5 2.5 0 0 1 19 8.5v8A2.5 2.5 0 0 1 16.5 19h-11A2.5 2.5 0 0 1 3 16.5Z',
    ),
    ['circle', { cx: 11, cy: 12, r: 3.2 }],
  ],
  lockTile: [
    ['rect', { x: 4, y: 10.5, width: 16, height: 10.5, rx: 2 }],
    ...p('M8 10.5V7.5a4 4 0 0 1 8 0v3'),
  ],
  lockRow: [
    ['rect', { x: 4.5, y: 10.5, width: 15, height: 9.5, rx: 2 }],
    ...p('M8 10.5V7.5a4 4 0 0 1 8 0v3'),
  ],
  lock: [['rect', { x: 5, y: 11, width: 14, height: 10, rx: 2 }], ...p('M8 11V7a4 4 0 018 0v4')],
  tutor: p('M4 5h16v11H9l-5 4z', 'M9 9.5h6M9 12.5h3.5'),
  bubble: p('M4 5h16v11H9l-5 4z'),
  lines: p('M4 5h16M4 12h16M4 19h10'),
  textLines: p('M4 6h16M4 12h16M4 18h10'),
  play: p('M8 6l10 6-10 6z'),
  list: p('M4 6h2M4 12h2M4 18h2M9 6h11M9 12h11M9 18h11'),
  signpost: [['rect', { x: 5, y: 3, width: 14, height: 14, rx: 4 }], ...p('M12 17v5', 'M9 10.5h6')],
  sheet: p('M6 3.5h9l4 4v13H6z', 'M14.5 3.5v4.5H19', 'M9 12h7M9 16h5'),
  scroll: p(
    'M8 21h10a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2H8',
    'M8 3a2 2 0 0 0-2 2v12a2 2 0 0 1-2 2 2 2 0 0 1-2-2',
    'M11 8h5M11 12h5M11 16h3',
  ),
  pin: [
    ...p('M12 21s-6.5-5.6-6.5-11a6.5 6.5 0 0 1 13 0c0 5.4-6.5 11-6.5 11z'),
    ['circle', { cx: 12, cy: 10, r: 2.4 }],
  ],
  road: p('M9.5 3h5l4 17H5.5z', 'M7.4 11h9.2M6.4 15.5h11.2', 'M3 20h18'),
  search: [['circle', { cx: 10.5, cy: 10.5, r: 6.5 }], ...p('M15.5 15.5 21 21')],
  flag: p('M5 21V4.5h13l-2.5 5 2.5 5H5'),
  book: p(
    'M3 6.5A2.5 2.5 0 015.5 4H10a2 2 0 012 2v13a1.6 1.6 0 00-1.6-1.6H3z',
    'M21 6.5A2.5 2.5 0 0018.5 4H14a2 2 0 00-2 2v13a1.6 1.6 0 011.6-1.6H21z',
  ),
  pencil: p('M16.5 4.5l3 3L8 19H5v-3z'),
  phone: p(
    'M5.5 4.5h3l2 5-2.2 1.4a11 11 0 0 0 4.8 4.8L14.5 13.5l5 2v3a1.5 1.5 0 0 1-1.7 1.5C10.9 19.4 4.6 13.1 4 6.2A1.5 1.5 0 0 1 5.5 4.5z',
  ),
  group: [
    ['circle', { cx: 9, cy: 8, r: 3.2 }],
    ...p('M3.5 19c.6-3 2.8-4.6 5.5-4.6S14 16 14.6 19'),
    ['circle', { cx: 17, cy: 9.5, r: 2.4 }],
    ...p('M15 15.4c1.8-.5 4 .5 4.6 3.6'),
  ],
  close: p('M5 5l14 14M19 5 5 19'),
  tabHome: p('M3 10.5 12 3l9 7.5V21H3z'),
  tabBilet: p('M4 5h16v14H4z', 'M8 9h8M8 13h5'),
  tabLibrary: p(
    'M4 4.5h6a2 2 0 0 1 2 2V21a2.5 2.5 0 0 0-2.5-2.5H4z',
    'M20 4.5h-6a2 2 0 0 0-2 2V21a2.5 2.5 0 0 1 2.5-2.5H20z',
  ),
  tabExam: p('M6 2.5h8.5L19 7v14.5H6z', 'M14 2.5V7h5', 'm9 14.5 2 2 4-4.5'),
  copy: [
    ['rect', { x: 9, y: 9, width: 11, height: 11, rx: 2 }],
    ...p('M15 5.5H6A1.5 1.5 0 0 0 4.5 7v9'),
  ],
  globe: p(
    'M12 3.5a8.5 8.5 0 1 0 0 17 8.5 8.5 0 0 0 0-17',
    'M3.5 12h17',
    'M12 3.5c2.5 2.6 2.5 14.4 0 17M12 3.5c-2.5 2.6-2.5 14.4 0 17',
  ),
  screen: p('M3.5 6h17v11h-17z', 'M9 20h6'),
  bell: p('M12 3.5a6 6 0 0 0-6 6v4l-1.5 3h15l-1.5-3v-4a6 6 0 0 0-6-6z', 'M10 19.5a2 2 0 0 0 4 0'),
  sunRow: p(
    'M12 7.5a4.5 4.5 0 1 0 0 9 4.5 4.5 0 0 0 0-9',
    'M12 2.5v2.5M12 19v2.5M2.5 12H5M19 12h2.5M5.2 5.2l1.8 1.8M17 17l1.8 1.8M18.8 5.2 17 7M7 17l-1.8 1.8',
  ),
  down: p('M12 4v10', 'M8 10.5l4 3.5 4-3.5', 'M4.5 18.5h15'),
  card: p('M3.5 6h17v12h-17z', 'M3.5 10h17', 'M7 14h4'),
  gauge: p('M4 17a8.5 8.5 0 1 1 16 0', 'M12 17l4-5'),
  timer: p('M12 20.5a7.5 7.5 0 1 0 0-15 7.5 7.5 0 0 0 0 15z', 'M12 9.5V13l2.5 1.5', 'M9 3.5h6'),
  ban: p('M12 3.5a8.5 8.5 0 1 0 0 17 8.5 8.5 0 0 0 0-17', 'M6.2 17.8 17.8 6.2'),
  warn: p('M12 4.5 20.5 19.5h-17z', 'M12 10v4', 'M12 16.6v.2'),
  ruler: p('M14.5 3.5 20.5 9.5 9.5 20.5 3.5 14.5z', 'M8 13l1.6 1.6M11 10l1.6 1.6M14 7l1.6 1.6'),
  box: p('M12 3.5 20 7.6v8.8L12 20.5 4 16.4V7.6z', 'M4 7.6 12 11.8l8-4.2', 'M12 11.8v8.7'),
  link: p(
    'M9.5 14.5 14.5 9.5',
    'M8.2 11 6 13.2a3.5 3.5 0 0 0 5 5L13.2 16',
    'M11 8.2 13.2 6a3.5 3.5 0 0 1 5 5L16 13.2',
  ),
  tyre: p(
    'M12 3.5a8.5 8.5 0 1 0 0 17 8.5 8.5 0 0 0 0-17',
    'M12 8.6a3.4 3.4 0 1 0 0 6.8 3.4 3.4 0 0 0 0-6.8',
    'M12 11.6v.2',
  ),
  wrench: p('M15.5 3.5a5 5 0 0 0-4.2 7.6L4 18.4 5.6 20l7.3-7.3A5 5 0 0 0 20 8.5l-3 1.2-1.7-1.7z'),
  grid: p('M4.5 4.5h6v6h-6zM13.5 4.5h6v6h-6zM4.5 13.5h6v6h-6zM13.5 13.5h6v6h-6z'),
  layers: p('M12 3.5 4 8l8 4.5L20 8z', 'M4 12.5 12 17l8-4.5', 'M4 16.5 12 21l8-4.5'),
  bulb: p('M9.5 18h5', 'M10 21h4', 'M12 3.5a5.5 5.5 0 0 0-3 10.1V18h6v-4.4a5.5 5.5 0 0 0-3-10.1z'),
  clock: p('M12 3.5a8.5 8.5 0 1 0 0 17 8.5 8.5 0 0 0 0-17', 'M12 7.5V12l3 2'),
  reset: p('M4.5 9.5A8 8 0 1 1 6 16', 'M4 4.5v5h5'),
  chat: p('M4.5 5.5h15v10h-9l-6 4z'),
  send: p('M3.5 12 20.5 4l-6 16.5-3.5-6z', 'M11 14.5 20.5 4'),
  star: p('M12 4l2.4 5 5.6.6-4.2 3.8 1.2 5.6-5-3-5 3 1.2-5.6L4 9.6 9.6 9z'),
  trash: p('M5.5 7.5h13', 'M9 7.5V5h6v2.5', 'M7 7.5 8 20h8l1-12.5'),
} satisfies Record<string, Part[]>;

export type GlyphName = keyof typeof GLYPHS;

type Props = Omit<SVGProps<SVGSVGElement>, 'name' | 'stroke'> & {
  name: GlyphName;
  size?: number;
  weight?: number;
  round?: boolean;
};

export function Glyph({ name, size = 18, weight = 2, round = true, ...rest }: Props) {
  return (
    <svg
      aria-hidden
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={weight}
      strokeLinecap={round ? 'round' : undefined}
      strokeLinejoin={round ? 'round' : undefined}
      {...rest}
    >
      {(GLYPHS[name] as Part[]).map(([tag, attrs], i) => {
        if (tag === 'circle') return <circle key={i} {...attrs} />;
        if (tag === 'rect') return <rect key={i} {...attrs} />;
        return <path key={i} {...attrs} />;
      })}
    </svg>
  );
}
